/**
 * Conditions on a calling rule: the "except when" rows on a work profile, and the
 * sentence that says what they will do.
 *
 * The rule itself is in internal/queue/conditions.go. A profile keeps its calling
 * rule and may list up to five exceptions, checked in order before it; the first
 * that is true calls the longest-waiting customer in its queue, and if none is the
 * rule decides. "This desk calls by priority, except when Cash has had somebody
 * waiting over fifteen minutes; then Cash first."
 *
 * There are four kinds, not two — the clock on the wall and how far past its own
 * target anybody has gone joined wait and length in Batch 2, for the reason
 * internal/queue/conditions.go's opening comment gives: each can be explained to
 * the customer it overtook.
 *
 * All of it lives here rather than in the screen for the reason lib/shares gives:
 * the web suite runs in Node with no DOM, so a refusal written into the form is a
 * refusal nothing tests.
 */

import { t, type StaffLanguage } from './staffText'
import { CONDITION_KINDS, type CallCondition, type ConditionKind } from './types'

/** The limits queue.MaxConditions and its neighbours hold the server to. */
export const CONDITIONS_MAX = 5
export const WAIT_MIN_MINUTES = 1
export const WAIT_MAX_MINUTES = 240
export const LENGTH_MIN = 1
export const LENGTH_MAX = 999
export const OVER_MIN_MINUTES = 0
export const OVER_MAX_MINUTES = 240
export const MINUTES_IN_DAY = 1440
export const LATE_MIN_MINUTES = 0
export const LATE_MAX_MINUTES = 240
export const EARLY_MAX_MINUTES = 240

/**
 * A condition with its keys in the order Go marshals queue.CallCondition.
 *
 * The screen decides a row has unsaved changes by comparing the draft with the
 * stored profile as text, so the order of the keys is part of the value: built any
 * other way, a condition read from the server and the same condition rebuilt here
 * would differ, and the row would claim to be unsaved. from and until are written
 * for every kind, never omitted, for the same reason: the server always sends
 * them (queue.CallCondition has no omitempty), so a condition missing them would
 * look unsaved from the moment it loaded.
 */
export function makeCondition(
  kind: ConditionKind,
  queueId: string,
  threshold: number,
  from = 0,
  until = 0,
): CallCondition {
  return { kind, queueId, threshold, from, until }
}

/**
 * The condition "Add a condition" starts with: fifteen minutes, five people,
 * lunchtime, the moment the target is missed, or the moment a booked customer's
 * time comes.
 *
 * Each is the example somebody says out loud, and each is inside the ranges, so a
 * condition that has just been added is one that can be saved as it stands.
 */
export function newCondition(kind: ConditionKind, queueId: string): CallCondition {
  switch (kind) {
    case 'wait_over':
      return makeCondition(kind, queueId, 900)
    case 'length_over':
      return makeCondition(kind, queueId, 5)
    case 'time_between':
      return makeCondition(kind, queueId, 0, 720, 840)
    case 'over_service_level':
      return makeCondition(kind, queueId, 0)
    case 'appointment_late':
      return makeCondition(kind, queueId, 0)
  }
}

/** The kinds whose threshold the screen asks for in minutes and keeps in seconds. */
const inMinutes = (k: ConditionKind) =>
  k === 'wait_over' || k === 'over_service_level' || k === 'appointment_late'

/** The number the box shows: minutes for a wait or a wait past the target,
 *  people for a length. A time of day has no threshold box. */
export function thresholdForInput(c: CallCondition): number {
  return inMinutes(c.kind) ? c.threshold / 60 : c.threshold
}

/** The number the server keeps, from what was typed in the box. */
export function thresholdFromInput(kind: ConditionKind, typed: number): number {
  return inMinutes(kind) ? typed * 60 : typed
}

/** The threshold box's own bounds, in the units it is shown in — minutes for a
 *  wait and a wait past the target, people for a length. A time of day has no
 *  threshold box, so its bounds are never read. */
export function thresholdBounds(kind: ConditionKind): { min: number; max: number } {
  switch (kind) {
    case 'wait_over':
      return { min: WAIT_MIN_MINUTES, max: WAIT_MAX_MINUTES }
    case 'length_over':
      return { min: LENGTH_MIN, max: LENGTH_MAX }
    case 'over_service_level':
      return { min: OVER_MIN_MINUTES, max: OVER_MAX_MINUTES }
    case 'appointment_late':
      return { min: LATE_MIN_MINUTES, max: LATE_MAX_MINUTES }
    case 'time_between':
      return { min: 0, max: 0 }
  }
}

/** Whether a kind asks for a single threshold, or a window of two times. */
export function conditionShape(kind: ConditionKind): 'threshold' | 'window' {
  return kind === 'time_between' ? 'window' : 'threshold'
}

/** A minute past midnight, said as the clock on the wall shows it. */
export function clockOf(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
}

/** A clock reading, kept as minutes past midnight — the same forgiveness
 *  Hours.tsx's toMinutes has: anything that does not parse is nought. */
export function minuteOf(clock: string): number {
  const [h, m] = clock.split(':')
  return (Number(h) || 0) * 60 + (Number(m) || 0)
}

/**
 * What is wrong with the list, in the order the server asks it, or null.
 *
 * The first fault only, as the server answers: the rows are few, and a person
 * fixes them one at a time.
 */
export function conditionsProblem(
  queueIds: readonly string[],
  conditions: readonly CallCondition[] | undefined,
  lang: StaffLanguage,
): string | null {
  const list = conditions ?? []
  if (list.length > CONDITIONS_MAX) return t('calling.cond.tooMany', lang, { n: CONDITIONS_MAX })
  const seen = new Set<string>()
  for (const c of list) {
    if (!(CONDITION_KINDS as readonly string[]).includes(c.kind)) return t('calling.cond.unknownKind', lang)
    if (!queueIds.includes(c.queueId)) return t('calling.cond.notServed', lang)
    if (c.kind === 'wait_over') {
      const minutes = c.threshold / 60
      if (!Number.isInteger(minutes) || minutes < WAIT_MIN_MINUTES || minutes > WAIT_MAX_MINUTES)
        return t('calling.cond.badWait', lang, { min: WAIT_MIN_MINUTES, max: WAIT_MAX_MINUTES })
    } else if (c.kind === 'length_over') {
      if (!Number.isInteger(c.threshold) || c.threshold < LENGTH_MIN || c.threshold > LENGTH_MAX)
        return t('calling.cond.badLength', lang, { min: LENGTH_MIN, max: LENGTH_MAX })
    } else if (c.kind === 'time_between') {
      if (
        !Number.isInteger(c.from) ||
        !Number.isInteger(c.until) ||
        c.from < 0 ||
        c.from >= MINUTES_IN_DAY ||
        c.until < 0 ||
        c.until >= MINUTES_IN_DAY ||
        c.from === c.until
      )
        return t('calling.cond.badWindow', lang)
    } else if (c.kind === 'over_service_level') {
      const minutes = c.threshold / 60
      if (!Number.isInteger(minutes) || minutes < OVER_MIN_MINUTES || minutes > OVER_MAX_MINUTES)
        return t('calling.cond.badOver', lang, { min: OVER_MIN_MINUTES, max: OVER_MAX_MINUTES })
    } else if (c.kind === 'appointment_late') {
      const minutes = c.threshold / 60
      if (!Number.isInteger(minutes) || minutes < LATE_MIN_MINUTES || minutes > LATE_MAX_MINUTES)
        return t('calling.cond.badLate', lang, { min: LATE_MIN_MINUTES, max: LATE_MAX_MINUTES })
    }
    const key = c.kind === 'time_between' ? `${c.kind} ${c.queueId} ${c.from} ${c.until}` : `${c.kind} ${c.queueId}`
    if (seen.has(key)) return t('calling.cond.twice', lang)
    seen.add(key)
  }
  return null
}

/**
 * The list as the draft holds it.
 *
 * A condition on a queue the profile no longer serves goes with the queue, so that
 * removing a queue does not leave behind an exception the server refuses and the
 * screen shows no row to correct. And nothing at all rather than an empty list when
 * there is none, because the server leaves the key out for a profile with no
 * exceptions and an empty list would be a difference nobody made.
 */
export function conditionsForSave(
  queueIds: readonly string[],
  conditions: readonly CallCondition[] | undefined,
): CallCondition[] | undefined {
  const kept = (conditions ?? []).filter((c) => queueIds.includes(c.queueId))
  return kept.length === 0 ? undefined : kept
}

/**
 * The list as it goes over the wire: always a list.
 *
 * The server reads an absent key as "this save did not ask about the conditions"
 * and keeps whatever the profile had. The draft holds nothing for none — see
 * conditionsForSave — so taking the last condition away has to be said out loud as
 * an empty list, or the save would answer 200 and the exception would stay.
 */
export function conditionsToSend(conditions: readonly CallCondition[] | undefined): CallCondition[] {
  return [...(conditions ?? [])]
}

/**
 * What the rows will do, in the words a manager would use at the desk.
 *
 * Empty when there are none, because the rule's own hint already says what the
 * desk does then. One exception reads as the sentence the design was written
 * around; several are said in the order they are checked, because the order is
 * the thing most likely to surprise somebody — the first that is true decides, and
 * a later one never gets asked.
 */
export function conditionsSentence(
  conditions: readonly CallCondition[],
  labelOf: (queueId: string) => string,
  lang: StaffLanguage,
): string {
  if (conditions.length === 0) return ''
  const otherwise = t('calling.cond.otherwise', lang)
  if (conditions.length === 1) {
    const c = conditions[0]!
    return t('calling.cond.one', lang, { trigger: trigger(c, labelOf, lang), queue: labelOf(c.queueId), otherwise })
  }
  const clauses = conditions.map((c) =>
    t('calling.cond.clause', lang, { trigger: trigger(c, labelOf, lang), queue: labelOf(c.queueId) }),
  )
  return t('calling.cond.many', lang, { clauses: clauses.join('; '), otherwise })
}

/** What has to be true for a condition to fire, as a clause. */
function trigger(c: CallCondition, labelOf: (queueId: string) => string, lang: StaffLanguage): string {
  const where = labelOf(c.queueId)
  if (c.kind === 'wait_over')
    return t('calling.cond.waitOver', lang, { duration: duration(c.threshold, lang), where })
  if (c.kind === 'time_between')
    return t('calling.cond.between', lang, {
      from: clockOf(c.from),
      until: clockOf(c.until),
      midnight: c.from > c.until ? t('calling.cond.midnight', lang) : '',
      where,
    })
  if (c.kind === 'over_service_level')
    return c.threshold === 0
      ? t('calling.cond.pastTarget', lang, { where })
      : t('calling.cond.pastTargetBy', lang, { duration: duration(c.threshold, lang), where })
  if (c.kind === 'appointment_late')
    return c.threshold === 0
      ? t('calling.cond.lateNow', lang, { where })
      : t('calling.cond.lateBy', lang, { duration: duration(c.threshold, lang), where })
  return t('calling.cond.lengthOver', lang, { n: c.threshold, where })
}

/** A wait as it is said: minutes under an hour, hours when it is whole hours,
 *  and both when it is not. */
function duration(seconds: number, lang: StaffLanguage): string {
  const minutes = Math.round(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return t('calling.dur.minutes', lang, { n: minutes })
  if (rest === 0) return t('calling.dur.hours', lang, { n: hours })
  return `${t('calling.dur.hours', lang, { n: hours })} ${t('calling.dur.minutes', lang, { n: rest })}`
}

/**
 * How early a desk on this profile may call a booked customer — see
 * queue.Profile.AppointmentEarlySec. The box shows minutes, and empty means any
 * time, which is what every profile did before bookings existed.
 */
export function earlyForInput(sec: number | null | undefined): string {
  return sec === null || sec === undefined ? '' : String(sec / 60)
}

/** What was typed, as the seconds the server keeps; null for an empty box. A
 *  typed value that is not a number comes back as NaN, which earlyProblem refuses. */
export function earlyFromInput(typed: string): number | null {
  const trimmed = typed.trim()
  return trimmed === '' ? null : Number(trimmed) * 60
}

/** The server's refusal, word for word, or null. */
export function earlyProblem(sec: number | null | undefined, lang: StaffLanguage): string | null {
  if (sec === null || sec === undefined) return null
  const minutes = sec / 60
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > EARLY_MAX_MINUTES)
    return t('calling.early.bad', lang, { max: EARLY_MAX_MINUTES })
  return null
}

/** Always said on a save: an absent key would leave a limit the form just cleared. */
export function earlyToSend(sec: number | null | undefined): number | null {
  return sec ?? null
}

/** A repeat interval a profile may hold, mirroring queue.RepeatCallMin and
 *  queue.RepeatCallMax: 0 is never. */
export const REPEAT_MIN_SEC = 10
export const REPEAT_MAX_SEC = 600

/** The server's refusal (internal/api checkProfile), word for word, or null. */
export function repeatProblem(sec: number | undefined, lang: StaffLanguage): string | null {
  if (sec === undefined || sec === 0) return null
  if (!Number.isInteger(sec) || sec < REPEAT_MIN_SEC || sec > REPEAT_MAX_SEC) return t('calling.repeat.bad', lang)
  return null
}
